'use client';

import KmxFireStroked from '@kmx/mui-icons/KmxFireStroked';
import KmxInfoCircleStroked from '@kmx/mui-icons/KmxInfoCircleStroked';
import { handleCalculatorSubmitted } from '@kmx/tagging';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import useAdobeDataContext from '../context/adobeDataContext';
import {
  useCalculateVehiclePrice,
  useCalculatorDefaults,
  useUpdatePreferences,
} from '../hooks/useCalculator';
import {
  type CreditRatingKey,
  creditKeyToRating,
  creditRatingToKey,
} from '../types';
import getStoreId from '../utils/getStoreId';
import {
  currencyFormat,
  numbersOnly,
  toFixedNoRounding,
} from '../utils/numbers';
import { createCreditRateOptions } from './createCreditRateOptions';
import Roller from './Roller';

interface ICalculatorProps {
  isPrequalLoading: boolean;
  monthlyPaymentOverride?: string;
  downPaymentOverride?: string;
  creditScoreOverride?: CreditRatingKey;
  termLengthOverride?: string;
  compact?: boolean;
}

export default function Calculator({
  isPrequalLoading,
  monthlyPaymentOverride,
  downPaymentOverride,
  creditScoreOverride,
  termLengthOverride,
  compact = false,
}: ICalculatorProps) {
  const adobeData = useAdobeDataContext();
  const [storeId, setStoreId] = useState(0);
  const [storeError, setStoreError] = useState(false);

  useEffect(() => {
    const discoveredStoreId = Number(getStoreId());
    if (Number.isSafeInteger(discoveredStoreId) && discoveredStoreId > 0) {
      setStoreId(discoveredStoreId);
      return;
    }
    let attempts = 0;
    const interval = window.setInterval(() => {
      const nextStoreId = Number(getStoreId());
      if (Number.isSafeInteger(nextStoreId) && nextStoreId > 0) {
        setStoreId(nextStoreId);
        window.clearInterval(interval);
      } else if (attempts >= 4) {
        setStoreError(true);
        window.clearInterval(interval);
      } else {
        attempts += 1;
      }
    }, 300);
    return () => window.clearInterval(interval);
  }, []);

  const {
    rates,
    preferences,
    termLengths,
    isLoading: defaultsLoading,
  } = useCalculatorDefaults({});
  const [monthlyPayment, setMonthlyPayment] = useState(
    currencyFormat(String(preferences.monthlyPayment)),
  );
  const [downPayment, setDownPayment] = useState(
    currencyFormat(String(preferences.downPayment)),
  );
  const [creditScore, setCreditScore] = useState<CreditRatingKey>(
    creditRatingToKey[preferences.creditRating],
  );
  const [termLength, setTermLength] = useState(preferences.termLength);
  const [formSeeded, setFormSeeded] = useState(false);
  const [canUpdatePreferences, setCanUpdatePreferences] = useState(false);
  const longestTermLength = termLengths[termLengths.length - 1];

  useEffect(() => {
    setMonthlyPayment(currencyFormat(preferences.monthlyPayment));
    setDownPayment(currencyFormat(preferences.downPayment));
    setTermLength(preferences.termLength);
    setCreditScore(creditRatingToKey[preferences.creditRating]);
  }, [
    preferences.creditRating,
    preferences.downPayment,
    preferences.monthlyPayment,
    preferences.termLength,
  ]);

  const { isLoading: calculationLoading, data: calculation } =
    useCalculateVehiclePrice({
      userStoreId: String(storeId),
      overridePreferences: {
        prefersLongestTermLength: termLength === longestTermLength,
        monthlyPayment: Number(numbersOnly(monthlyPayment)),
        termLength,
        downPayment: Number(numbersOnly(downPayment)),
        creditRating: creditKeyToRating[creditScore],
      },
    });
  const vehiclePrice = calculation?.calculatedVehiclePrice ?? null;
  const totalFinancedAmount = calculation?.calculatedTotalFinanceAmount ?? null;
  const apr = calculation?.calculationDetails.apr ?? null;
  const taxesAndFees = calculation?.calculationDetails.taxesAndFees ?? null;
  const taxState = calculation?.calculationDetails.taxState ?? '';
  const calculatorLoading = defaultsLoading || calculationLoading;

  useEffect(() => {
    if (!formSeeded) {
      if (
        monthlyPaymentOverride ||
        downPaymentOverride ||
        creditScoreOverride ||
        termLengthOverride
      ) {
        if (monthlyPaymentOverride) {
          setMonthlyPayment(
            currencyFormat(numbersOnly(monthlyPaymentOverride)),
          );
        }
        if (downPaymentOverride) {
          setDownPayment(currencyFormat(numbersOnly(downPaymentOverride)));
        }
        if (creditScoreOverride) {
          setCreditScore(creditScoreOverride);
        }
        if (termLengthOverride) {
          setTermLength(Number(termLengthOverride));
        }
        setFormSeeded(true);
        return;
      }
      if (!defaultsLoading) {
        setMonthlyPayment(currencyFormat(String(preferences.monthlyPayment)));
        setDownPayment(currencyFormat(String(preferences.downPayment)));
        setCreditScore(creditRatingToKey[preferences.creditRating]);
        setTermLength(preferences.termLength);
        setFormSeeded(true);
      }
    }
  }, [
    preferences,
    defaultsLoading,
    formSeeded,
    monthlyPaymentOverride,
    downPaymentOverride,
    creditScoreOverride,
    termLengthOverride,
  ]);

  const updatePreferences = useUpdatePreferences();
  useEffect(() => {
    if (canUpdatePreferences) {
      void updatePreferences({
        prefersLongestTermLength: termLength === longestTermLength,
        monthlyPayment: Number(numbersOnly(monthlyPayment)),
        termLength,
        downPayment: Number(numbersOnly(downPayment)),
        creditRating: creditKeyToRating[creditScore],
        hasInteraction: true,
      });
    }
  }, [
    updatePreferences,
    canUpdatePreferences,
    termLength,
    longestTermLength,
    monthlyPayment,
    downPayment,
    creditScore,
  ]);

  const isReady = !isPrequalLoading && !calculatorLoading && formSeeded;
  const hasFirstInteraction = useRef(false);
  const trackFirstInteraction = useCallback(() => {
    setCanUpdatePreferences(true);
    if (!hasFirstInteraction.current) {
      hasFirstInteraction.current = true;
      window.adobeDataLayer?.push({
        event: 'calculatorInitiated',
        module: { moduleExperience: adobeData.originPage },
      });
    }
  }, [adobeData.originPage]);

  const handleMonthlyPaymentChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    trackFirstInteraction();
    const numbers = numbersOnly(event.target.value);
    const numericValue = Number(numbers);
    if (Number.isNaN(numericValue) || numericValue <= 0) {
      setMonthlyPayment(currencyFormat('0'));
      return;
    }
    if (numericValue <= 5000) {
      setMonthlyPayment(currencyFormat(numbers));
    }
  };

  const handleDownPaymentChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    trackFirstInteraction();
    const numbers = numbersOnly(event.target.value);
    const numericValue = Number(numbers);
    if (Number.isNaN(numericValue) || numericValue < 0) {
      setDownPayment(currencyFormat('0'));
      return;
    }
    setDownPayment(currencyFormat(numbers));
  };

  const handleCreditScoreChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value) {
      trackFirstInteraction();
      setCreditScore(event.target.value as CreditRatingKey);
    }
  };

  const handleTermLengthChange = (
    _event: MouseEvent<HTMLElement>,
    value: number | null,
  ): void => {
    if (value !== null) {
      trackFirstInteraction();
      setTermLength(value);
    }
  };

  const termButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const handleTermKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ): void => {
    let nextIndex = index;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (index + 1) % termLengths.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (index - 1 + termLengths.length) % termLengths.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = termLengths.length - 1;
    } else {
      return;
    }
    const nextTermLength = termLengths[nextIndex];
    if (nextTermLength === undefined) {
      return;
    }
    event.preventDefault();
    setTermLength(nextTermLength);
    termButtonRefs.current[nextIndex]?.focus();
  };

  const [disclaimerAnchor, setDisclaimerAnchor] =
    useState<HTMLButtonElement | null>(null);
  const prequalUrl = `/pre-qual/app?${adobeData.opQueryString}&term=${termLength}&downpayment=${numbersOnly(downPayment)}&rate=${creditScore}&vehicleprice=${vehiclePrice ? Math.round(vehiclePrice) : ''}`;

  let renderedVehiclePrice: ReactNode = toFixedNoRounding(0, 0);
  if (vehiclePrice !== null && vehiclePrice >= 0) {
    renderedVehiclePrice = <Roller targetValue={vehiclePrice} />;
  } else if (vehiclePrice !== null && vehiclePrice < 0) {
    renderedVehiclePrice = 'N/A';
  }

  if (storeError) {
    return null;
  }

  return (
    <Card
      sx={{
        borderRadius: '32px',
        maxWidth: '1536px',
        width: '100%',
        margin: '0 auto',
      }}
      variant="outlined"
      id="pfc-v2-calculator"
    >
      <Stack
        direction={compact ? 'column' : { xs: 'column', md: 'row' }}
        sx={{ gap: 0 }}
      >
        <Stack
          direction="column"
          sx={{
            gap: compact ? 4 : { xs: 4, md: 2 },
            alignItems: compact ? 'center' : { xs: 'center', md: 'flex-start' },
            flex: '1 0 0',
            padding: compact
              ? '32px 40px 16px 40px'
              : { xs: '32px 40px 16px 40px', md: '40px 40px 32px 40px' },
          }}
        >
          <Stack
            direction="column"
            sx={{
              gap: 1,
              alignItems: compact
                ? 'center'
                : { xs: 'center', md: 'flex-start' },
            }}
          >
            {isReady ? (
              <Chip
                label="Over 97% get pre-qualified"
                size="medium"
                color="primary"
                variant="weak"
                icon={<KmxFireStroked />}
              />
            ) : (
              <Skeleton variant="rounded" width={220} height={32} />
            )}
            {isReady ? (
              <Typography
                component="h2"
                variant="h5"
                sx={{ textAlign: 'center' }}
                id="pfc-v2-main-heading"
              >
                Find cars that fit your life and budget
              </Typography>
            ) : (
              <Skeleton variant="text" width="80%" height={32} />
            )}
          </Stack>
          <Stack sx={{ gap: 2, width: '100%' }}>
            <Stack
              direction={compact ? 'column' : { xs: 'column', md: 'row' }}
              sx={{ gap: 2, width: '100%' }}
            >
              {isReady ? (
                <TextField
                  id="pfc-v2-monthly-payment"
                  label="Monthly payment"
                  value={monthlyPayment}
                  onChange={handleMonthlyPaymentChange}
                  variant="outlined"
                  size="medium"
                  slotProps={{ htmlInput: { maxLength: 7 } }}
                  fullWidth
                />
              ) : (
                <Skeleton variant="rounded" width="100%" height={56} />
              )}
              {isReady ? (
                <TextField
                  id="pfc-v2-down-payment"
                  label="Down payment"
                  value={downPayment}
                  onChange={handleDownPaymentChange}
                  variant="outlined"
                  size="medium"
                  slotProps={{ htmlInput: { maxLength: 7 } }}
                  fullWidth
                />
              ) : (
                <Skeleton variant="rounded" width="100%" height={56} />
              )}
              {isReady && rates ? (
                <TextField
                  id="pfc-v2-credit-score"
                  select
                  label="Credit score"
                  value={creditScore}
                  onChange={handleCreditScoreChange}
                  slotProps={{ select: { native: true } }}
                  fullWidth
                >
                  {rates.map(createCreditRateOptions)}
                </TextField>
              ) : (
                <Skeleton variant="rounded" width="100%" height={56} />
              )}
            </Stack>
            <Stack
              direction="column"
              sx={{
                gap: 0.5,
                alignItems: 'flex-start',
                width: '100%',
                '&:focus-within #pfc-v2-term-length-label': {
                  color: 'primary.main',
                },
              }}
            >
              {isReady ? (
                <>
                  <Typography
                    id="pfc-v2-term-length-label"
                    variant="caption"
                    color="textSecondary"
                    component="span"
                    sx={{ transition: 'color 0.15s ease-in-out' }}
                  >
                    Term length (months)
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    size="small"
                    exclusive
                    value={termLength}
                    onChange={handleTermLengthChange}
                    aria-labelledby="pfc-v2-term-length-label"
                    role="radiogroup"
                    fullWidth
                    sx={{
                      '& .MuiToggleButton-root': {
                        fontSize: '18px',
                        fontWeight: 700,
                        lineHeight: '175%',
                      },
                    }}
                  >
                    {termLengths.map((value, index) => {
                      const selected = value === termLength;
                      return (
                        <ToggleButton
                          key={value}
                          value={value}
                          ref={element => {
                            termButtonRefs.current[index] = element;
                          }}
                          tabIndex={selected ? 0 : -1}
                          role="radio"
                          aria-checked={selected}
                          aria-label={`${value} months`}
                          onKeyDown={event => handleTermKeyDown(event, index)}
                        >
                          {value}
                        </ToggleButton>
                      );
                    })}
                  </ToggleButtonGroup>
                </>
              ) : (
                <Skeleton variant="rounded" width="100%" height={40} />
              )}
            </Stack>
          </Stack>
        </Stack>
        <Divider
          sx={{
            display: compact ? 'block' : { xs: 'block', md: 'none' },
            borderBottomWidth: 'thin',
            marginInline: '-32px',
          }}
        />
        <Stack
          direction="column"
          sx={{
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
            padding: compact
              ? '32px 40px 32px 40px'
              : { xs: '32px 40px 32px 40px', md: '48px 40px 40px 40px' },
            flexShrink: 0,
          }}
        >
          <Stack direction="column" sx={{ alignItems: 'center', gap: 0.5 }}>
            <Stack
              direction="row"
              sx={{ justifyContent: 'center', alignItems: 'center', gap: 0.5 }}
            >
              {isReady ? (
                <Typography component="p" variant="h6">
                  Est. car price
                </Typography>
              ) : (
                <Skeleton variant="text" width={100} />
              )}
            </Stack>
            {isReady ? (
              <Typography component="p" variant="h4" id="pfc-v2-car-price">
                {renderedVehiclePrice}
              </Typography>
            ) : (
              <Skeleton variant="text" width={200} height={60} />
            )}
            {isReady && rates ? (
              <Stack direction="row" sx={{ gap: 0.5 }}>
                <Typography variant="body1" id="pfc-v2-apr">
                  Est. APR {apr}%
                </Typography>
                {typeof taxesAndFees === 'number' && (
                  <>
                    <IconButton
                      onClick={event =>
                        setDisclaimerAnchor(event.currentTarget)
                      }
                      size="medium"
                      aria-label="Display estimate disclaimer"
                      sx={{
                        color: '#001333',
                        '&.Mui-focusVisible': { outlineOffset: '-6px' },
                        padding: 0,
                      }}
                    >
                      <KmxInfoCircleStroked
                        color="inherit"
                        fontSize="inherit"
                      />
                    </IconButton>
                    <Popover
                      open={Boolean(disclaimerAnchor)}
                      anchorEl={disclaimerAnchor}
                      onClose={() => setDisclaimerAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      sx={{ marginTop: '8px' }}
                    >
                      <Typography
                        variant="caption"
                        component="p"
                        sx={{ maxWidth: '320px', padding: '16px' }}
                      >
                        Estimate is based on the information you provide and a
                        total finance amount of{' '}
                        {totalFinancedAmount !== null
                          ? currencyFormat(totalFinancedAmount)
                          : ''}
                        , which includes {currencyFormat(taxesAndFees)} in{' '}
                        {taxState} estimated tax, title, and fees. Estimated
                        values are for illustration purposes only, and
                        don&apos;t constitute an advertisement or offer of
                        specific credit terms. APRs and terms used in estimates
                        may be unavailable based on vehicle, state of purchase,
                        or your credit profile. Actual credit terms are subject
                        to approval and availability.
                      </Typography>
                    </Popover>
                  </>
                )}
              </Stack>
            ) : (
              <Skeleton variant="text" width={80} />
            )}
          </Stack>
          <Stack
            direction="column"
            sx={{ gap: 0.5, alignItems: 'center', width: '100%' }}
          >
            {isReady ? (
              <>
                <Button
                  href={prequalUrl}
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={event =>
                    handleCalculatorSubmitted({
                      event,
                      financeCalculator: {
                        creditRating: creditKeyToRating[creditScore],
                        downPayment,
                        interestRate: apr?.toString(),
                        monthlyPayment,
                        price: vehiclePrice?.toString(),
                        state: taxState,
                        termLength: termLength?.toString(),
                      },
                    })
                  }
                  id="pfc-v2-cta"
                  sx={{ padding: '8px 24px', width: { md: '256px' } }}
                >
                  Get pre-qualified
                </Button>
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                  id="pfc-v2-disclaimer"
                >
                  No impact to your credit score
                </Typography>
              </>
            ) : (
              <Skeleton variant="rounded" width="100%" height={42} />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}
